import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { BookOpen, Pencil, RefreshCw, X } from 'lucide-react';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const GROUPS = [
  { value: 'AVE', label: 'Aves' },
  { value: 'MAM', label: 'Mamíferos' },
  { value: 'REPT', label: 'Répteis' },
];

export default function SpeciesCatalogPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const emptyForm = {
    scientific_name: '',
    common_name: '',
    animal_group: 'AVE',
    taxonomic_order: '',
    notes: '',
    active: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [editingItemId, setEditingItemId] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('include_inactive', 'true');
      if (search.trim()) params.set('search', search.trim());
      if (groupFilter !== 'all') params.set('animal_group', groupFilter);
      const response = await axios.get(`${API}/species-catalog?${params.toString()}`);
      setItems(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar catálogo de espécies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const counts = useMemo(() => (
    items.reduce((acc, item) => {
      acc.total += 1;
      acc[item.animal_group] = (acc[item.animal_group] || 0) + 1;
      if (item.active === false) acc.inactive += 1;
      return acc;
    }, { total: 0, inactive: 0 })
  ), [items]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingItemId(null);
  };

  const handleEdit = (item) => {
    setEditingItemId(item.id);
    setForm({
      scientific_name: item.scientific_name || '',
      common_name: item.common_name || '',
      animal_group: item.animal_group || 'AVE',
      taxonomic_order: item.taxonomic_order || '',
      notes: item.notes || '',
      active: item.active !== false,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.scientific_name.trim() || !form.common_name.trim() || !form.animal_group) {
      toast.error('Preencha nome científico, nome popular e grupo');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        scientific_name: form.scientific_name.trim(),
        common_name: form.common_name.trim(),
        animal_group: form.animal_group,
        taxonomic_order: form.taxonomic_order.trim() || null,
        notes: form.notes.trim() || null,
        active: form.active,
      };
      if (editingItemId) {
        await axios.put(`${API}/species-catalog/${editingItemId}`, payload);
        toast.success('Espécie atualizada');
      } else {
        await axios.post(`${API}/species-catalog`, payload);
        toast.success('Espécie cadastrada');
      }
      resetForm();
      await fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao ${editingItemId ? 'atualizar' : 'cadastrar'} espécie`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      <header className="sticky top-0 z-40 border-b border-emerald-100 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {SHOW_INSTITUTO_BW_BRANDING && (
              <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-12 w-12 flex-shrink-0 object-contain" />
            )}
            <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
              <h1 className="truncate text-lg font-bold text-emerald-800 md:text-xl">Catálogo de Espécies</h1>
              <p className="text-xs text-emerald-500">Cadastro administrativo para padronizar admissões</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={fetchItems}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/')}>Voltar</Button>
            <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={logout}>Sair</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[360px_1fr]">
        <section className="space-y-4">
          <Card className="border-emerald-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-900">
                <BookOpen className="h-5 w-5" />
                {editingItemId ? 'Editar espécie' : 'Nova espécie'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nome científico *</Label>
                  <Input value={form.scientific_name} onChange={(e) => updateForm('scientific_name', e.target.value)} />
                </div>
                <div>
                  <Label>Nome popular *</Label>
                  <Input value={form.common_name} onChange={(e) => updateForm('common_name', e.target.value)} />
                </div>
                <div>
                  <Label>Grupo *</Label>
                  <Select value={form.animal_group} onValueChange={(value) => updateForm('animal_group', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GROUPS.map((group) => (
                        <SelectItem key={group.value} value={group.value}>{group.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ordem</Label>
                  <Input value={form.taxonomic_order} onChange={(e) => updateForm('taxonomic_order', e.target.value)} />
                </div>
                <div>
                  <Label>Observações</Label>
                  <Input value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.active ? 'active' : 'inactive'} onValueChange={(value) => updateForm('active', value === 'active')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativa</SelectItem>
                      <SelectItem value="inactive">Inativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    {saving ? 'Salvando...' : editingItemId ? 'Salvar alterações' : 'Cadastrar'}
                  </Button>
                  {editingItemId && (
                    <Button type="button" variant="outline" onClick={resetForm}>
                      <X className="mr-2 h-4 w-4" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-emerald-100">
            <CardContent className="grid grid-cols-2 gap-3 pt-6 text-sm">
              <div><span className="text-gray-500">Total:</span> <strong>{counts.total}</strong></div>
              <div><span className="text-gray-500">Inativas:</span> <strong>{counts.inactive}</strong></div>
              <div><span className="text-gray-500">Aves:</span> <strong>{counts.AVE || 0}</strong></div>
              <div><span className="text-gray-500">Mamíferos:</span> <strong>{counts.MAM || 0}</strong></div>
              <div><span className="text-gray-500">Répteis:</span> <strong>{counts.REPT || 0}</strong></div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <Card className="border-emerald-100">
            <CardContent className="grid gap-4 pt-6 md:grid-cols-[1fr_180px_auto]">
              <div>
                <Label>Busca</Label>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome científico ou popular" />
              </div>
              <div>
                <Label>Grupo</Label>
                <Select value={groupFilter} onValueChange={setGroupFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {GROUPS.map((group) => (
                      <SelectItem key={group.value} value={group.value}>{group.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button type="button" className="w-full" onClick={fetchItems}>Filtrar</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-emerald-100">
            <CardContent className="pt-6">
              {loading ? (
                <p className="py-8 text-center text-gray-500">Carregando...</p>
              ) : items.length === 0 ? (
                <p className="py-8 text-center text-gray-500">Nenhuma espécie encontrada</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-gray-500">
                        <th className="py-2 pr-3">Nome científico</th>
                        <th className="py-2 pr-3">Nome popular</th>
                        <th className="py-2 pr-3">Grupo</th>
                        <th className="py-2 pr-3">Origem</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="py-3 pr-3 font-medium text-emerald-900">{item.scientific_name}</td>
                          <td className="py-3 pr-3">{item.common_name}</td>
                          <td className="py-3 pr-3">{item.animal_group}</td>
                          <td className="py-3 pr-3 text-gray-600">{item.source_sheet || item.notes || '-'}</td>
                          <td className="py-3 pr-3">
                            <Badge className={item.active === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-800'}>
                              {item.active === false ? 'Inativa' : 'Ativa'}
                            </Badge>
                          </td>
                          <td className="py-3 text-right">
                            <Button type="button" variant="outline" size="sm" onClick={() => handleEdit(item)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Editar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
